import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  applyPageMeta,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  removeHreflang,
} from '../../helpers/seoMeta';

/**
 * Metadatos (title/description/canonical/OG/Twitter/robots) por ruta.
 *
 * Antes de esto TODAS las rutas servían y mostraban el mismo <title>, que es
 * el problema SEO más caro de una SPA: títulos duplicados a nivel de sitio.
 * Google ejecuta JS, así que basta con actualizarlos en cliente; no hace falta
 * prerender para esto.
 *
 * Las rutas de anuncio (`/ad/:id`) se excluyen: las gestiona `SEO` dentro de
 * `AdPage` con los datos reales del anuncio.
 */
const AD_DETAIL = /^\/ad\/[^/]+\/?$/;

const ROUTES = [
  {
    test: /^\/$/,
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  {
    test: /^\/ads\/?$/,
    title: 'Browse all ads | Terserah',
    description:
      'Search thousands of classified ads in Indonesia: electronics, phones, vehicles, fashion, home and more. Filter by category, price and location.',
  },
  {
    test: /^\/about\/?$/,
    title: 'About Terserah | Terserah',
    description:
      'Terserah is a marketplace to buy and sell locally in Indonesia. Learn how it works, how to post an ad for free and how we keep buyers and sellers safe.',
  },
  {
    test: /^\/privacy\/?$/,
    title: 'Privacy policy | Terserah',
    description: 'How Terserah collects, uses and protects your personal data.',
  },
  {
    test: /^\/terms\/?$/,
    title: 'Terms of use | Terserah',
    description: 'The rules for buying, selling and posting ads on Terserah.',
  },
  {
    test: /^\/user\/[^/]+\/?$/,
    title: 'Seller profile | Terserah',
    description: 'See this seller’s ads, ratings and reviews on Terserah.',
  },
  {
    test: /^\/signin\/?$/,
    title: 'Sign in | Terserah',
    description: 'Sign in to manage your ads, messages and offers on Terserah.',
    noindex: true,
  },
  {
    test: /^\/signup\/?$/,
    title: 'Create a free account | Terserah',
    description: 'Create your free Terserah account and start buying and selling in Indonesia.',
    noindex: true,
  },
  {
    test: /^\/(post-an-add|edit-ad\/[^/]+)\/?$/,
    title: 'Post an ad | Terserah',
    description: 'Post your ad on Terserah for free.',
    noindex: true,
  },
  {
    test: /^\/(my-account|favorites|messages|dashboard|offers|statistics|saved-searches|profile-settings|forgot-password|reset-password|reset-password-code|verify-email|verify-2fa|2fa-settings|complete-profile|auth)\b/,
    title: 'My account | Terserah',
    noindex: true,
  },
  {
    test: /^\/admin\b/,
    title: 'Admin | Terserah',
    noindex: true,
  },
];

const NOT_FOUND = {
  title: 'Page not found | Terserah',
  description: DEFAULT_DESCRIPTION,
  noindex: true,
};

/**
 * Devuelve los metadatos de una ruta, o `null` si la ruta la gestiona otro
 * componente (detalle de anuncio). Exportado para poder testearlo sin render.
 */
export const metaForPath = (pathname) => {
  if (AD_DETAIL.test(pathname)) return null;
  return ROUTES.find((r) => r.test.test(pathname)) || NOT_FOUND;
};

export { ROUTES };

const RouteMeta = () => {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];

  useEffect(() => {
    // El detalle de anuncio tiene su propio componente SEO con datos reales.
    const meta = metaForPath(pathname);
    if (!meta) return;

    removeHreflang();
    applyPageMeta({
      title: meta.title,
      description: meta.description,
      path: pathname,
      lang,
      noindex: Boolean(meta.noindex),
    });
  }, [pathname, lang]);

  return null;
};

export default RouteMeta;
